import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  FormHelperText,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { RegisterRequest } from '../types/auth';
import { apiErrorMessage } from '../utils/apiErrors';
import { DOB_MIN, dateOfBirthError, dobMax } from '../utils/dateOfBirth';
import {
  PASSWORD_MIN_LENGTH,
  hasDigit,
  hasLowerCase,
  hasNonAlphanumeric,
  hasUpperCase,
  hasWhitespace,
} from '../utils/passwordPolicy';

interface RegisterProps {
  onSwitchToLogin: () => void;
  onBack?: () => void;
}

interface FieldErrors {
  name?: string;
  email?: string;
  phone?: string;
  dateOfBirth?: string;
  password?: string;
  confirmPassword?: string;
  billingName?: string;
  billingZipCode?: string;
  billingCity?: string;
  billingStreetAddress?: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const TERMS_TEXT = `RÉSZVÉTELI SZABÁLYZAT

A jelentkezéssel a résztvevő, illetve kiskorú esetén annak szülője, nevelésre jogosultja, törvényes képviselője kijelenti, hogy a résztvevő egészséges, sportfeladatokkal terhelhető, betegségben nem szenved.

A jelentkezéssel a résztvevő, illetve kiskorú esetén annak szülője, nevelésre jogosultja, törvényes képviselője kijelenti, hogy kisebb sportsérüléseket az edzők elláthatnak.

A jelentkezéssel résztvevőként, illetve kiskorú esetén annak szülője, nevelésre jogosultja, törvényes képviselőjeként nyilatkozom, hogy a Budakörnyéki Sportegyesület céljait pártolom, támogatom, és kérem, hogy vegyenek fel az Egyesület pártoló tagjai közé.

Résztvevőként, illetve kiskorú esetén annak szülője, nevelésre jogosultja, törvényes képviselőjeként hozzájárulok az edzésekről, foglalkozásokról készült fényképeknek és/vagy videóknak Egyesületünk saját honlapjain, illetve közösségi oldalain való közzétételéhez.

A kért adatokat bizalmasan kezeljük és harmadik félnek nem adjuk ki.`;

const Register: React.FC<RegisterProps> = ({ onSwitchToLogin, onBack }) => {
  const { register, loading } = useAuth();
  const [formData, setFormData] = useState<RegisterRequest>({
    name: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    password: '',
    confirmPassword: '',
    billingName: '',
    billingZipCode: '',
    billingCity: '',
    billingStreetAddress: '',
    taxNumber: '',
    acceptTerms: false,
  });
  const [error, setError] = useState<string>('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isRegistered, setIsRegistered] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [termsDialogOpen, setTermsDialogOpen] = useState(false);

  const validateField = (name: string, value: string, allData?: RegisterRequest): string => {
    const data = allData || formData;
    switch (name) {
      case 'name':
        if (!value.trim()) return 'A játékos neve kötelező';
        if (value.trim().length < 2) return 'A név legalább 2 karakter legyen';
        if (value.length > 100) return 'A név maximum 100 karakter lehet';
        return '';
      case 'email':
        if (!value.trim()) return 'Az e-mail cím kötelező';
        if (!EMAIL_REGEX.test(value)) return 'Érvénytelen e-mail cím formátum';
        if (value.length > 255) return 'Az e-mail cím maximum 255 karakter lehet';
        return '';
      case 'phone':
        if (!value.trim()) return 'A telefonszám kötelező';
        if (value.length > 20) return 'A telefonszám maximum 20 karakter lehet';
        return '';
      case 'dateOfBirth':
        return dateOfBirthError(value);
      case 'password':
        // A JELSZÓ-SZABÁLY EGY HELYEN, a szerverrel egyezően [K-3, 2026-09-16] — lásd
        // `src/utils/passwordPolicy.ts` (ott a mért lelet: a régi, FEHÉRLISTÁS regex a
        // `Pinyó2026!`-t, `Pinyo-2026a`-t, `Pinyo_2026a`-t is elutasította, miközben a
        // hibaszöveg csak „speciális karaktert" kért).
        // A mezőnkénti mondatok megmaradnak (a felhasználó így tudja, MELYIK feltétel hiányzik),
        // de a FELTÉTELEK a közös modulból jönnek — kliens és szerver nem csúszhat el.
        if (!value) return 'A jelszó kötelező';
        if (value.length < PASSWORD_MIN_LENGTH) return `A jelszó legalább ${PASSWORD_MIN_LENGTH} karakter legyen`;
        if (hasWhitespace(value)) return 'A jelszó nem tartalmazhat szóközt';
        if (!hasLowerCase(value)) return 'A jelszónak tartalmaznia kell kisbetűt';
        if (!hasUpperCase(value)) return 'A jelszónak tartalmaznia kell nagybetűt';
        if (!hasDigit(value)) return 'A jelszónak tartalmaznia kell számot';
        if (!hasNonAlphanumeric(value))
          return 'A jelszónak tartalmaznia kell legalább egy nem betű/szám karaktert (pl. ! ? - _ .)';
        return '';
      case 'confirmPassword':
        if (!value) return 'A jelszó megerősítése kötelező';
        if (value !== data.password) return 'A jelszavak nem egyeznek';
        return '';
      case 'billingName':
        if (!value.trim()) return 'A számlázási név kötelező';
        if (value.length > 200) return 'A számlázási név maximum 200 karakter lehet';
        return '';
      case 'billingZipCode':
        if (!value.trim()) return 'Az irányítószám kötelező';
        if (value.length > 10) return 'Az irányítószám maximum 10 karakter lehet';
        return '';
      case 'billingCity':
        if (!value.trim()) return 'A város kötelező';
        if (value.length > 100) return 'A város maximum 100 karakter lehet';
        return '';
      case 'billingStreetAddress':
        if (!value.trim()) return 'Az utca, házszám kötelező';
        if (value.length > 200) return 'Az utca, házszám maximum 200 karakter lehet';
        return '';
      default:
        return '';
    }
  };

  const validateAll = (data: RegisterRequest): FieldErrors => {
    const errors: FieldErrors = {};
    const nameErr = validateField('name', data.name, data);
    const emailErr = validateField('email', data.email, data);
    const phoneErr = validateField('phone', data.phone, data);
    const dateOfBirthErr = validateField('dateOfBirth', data.dateOfBirth, data);
    const passwordErr = validateField('password', data.password, data);
    const confirmErr = validateField('confirmPassword', data.confirmPassword, data);
    const billingNameErr = validateField('billingName', data.billingName, data);
    const billingZipCodeErr = validateField('billingZipCode', data.billingZipCode, data);
    const billingCityErr = validateField('billingCity', data.billingCity, data);
    const billingStreetAddressErr = validateField('billingStreetAddress', data.billingStreetAddress, data);
    if (nameErr) errors.name = nameErr;
    if (emailErr) errors.email = emailErr;
    if (phoneErr) errors.phone = phoneErr;
    if (dateOfBirthErr) errors.dateOfBirth = dateOfBirthErr;
    if (passwordErr) errors.password = passwordErr;
    if (confirmErr) errors.confirmPassword = confirmErr;
    if (billingNameErr) errors.billingName = billingNameErr;
    if (billingZipCodeErr) errors.billingZipCode = billingZipCodeErr;
    if (billingCityErr) errors.billingCity = billingCityErr;
    if (billingStreetAddressErr) errors.billingStreetAddress = billingStreetAddressErr;
    return errors;
  };

  /**
   * A submit gomb letiltásának oka(i) emberi nyelven — a felhasználó eddig nem
   * látta, miért marad inaktív a gomb, ha minden mezőt helyesen töltött ki, de
   * elfelejtette bejelölni a részvételi szabályzatot (vagy fordítva).
   */
  const getMissingRequirements = (): string[] => {
    const errors = validateAll(formData);
    const missing: string[] = [];
    if (errors.name) missing.push('játékos neve');
    if (errors.email) missing.push('e-mail cím');
    if (errors.phone) missing.push('telefonszám');
    if (errors.dateOfBirth) missing.push('születési dátum');
    if (errors.password) missing.push('jelszó');
    if (errors.confirmPassword) missing.push('jelszó megerősítése');
    if (errors.billingName) missing.push('számlázási név');
    if (errors.billingZipCode) missing.push('irányítószám');
    if (errors.billingCity) missing.push('város');
    if (errors.billingStreetAddress) missing.push('utca, házszám');
    if (!formData.acceptTerms) missing.push('részvételi szabályzat elfogadása');
    return missing;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const newData = { ...formData, [name]: value };
    setFormData(newData);

    // Validate the changed field if it was already touched
    if (touched[name]) {
      const err = validateField(name, value, newData);
      setFieldErrors(prev => ({ ...prev, [name]: err || undefined }));
    }

    // Re-validate confirmPassword when password changes
    if (name === 'password' && touched['confirmPassword'] && newData.confirmPassword) {
      const confirmErr = validateField('confirmPassword', newData.confirmPassword, newData);
      setFieldErrors(prev => ({ ...prev, confirmPassword: confirmErr || undefined }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const err = validateField(name, value);
    setFieldErrors(prev => ({ ...prev, [name]: err || undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Mark all fields as touched
    setTouched({ name: true, email: true, phone: true, dateOfBirth: true, password: true, confirmPassword: true, billingName: true, billingZipCode: true, billingCity: true, billingStreetAddress: true });

    // Validate all fields
    const errors = validateAll(formData);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    if (!formData.acceptTerms) {
      setError('A részvételi szabályzat elfogadása kötelező');
      return;
    }

    try {
      await register(formData);
      setIsRegistered(true);
    } catch (err: any) {
      // 🔴 ANGOL ASP.NET-SZÖVEG SZIVÁRGÁSA — JAVÍTVA [2026-09-14, B1 lelet].
      //
      // MI VOLT A BAJ: itt egy SAJÁT, a közös szűrőt teljesen MELLŐZŐ ág állt, ami a
      // `data.errors` szótárat KÖZVETLENÜL fűzte össze:
      //     const messages = Object.values(validationErrors).flat().join('. ');
      // A szótárba viszont a keretrendszer SAJÁT, ANGOL alapszövegei is bekerülnek, ha egy
      // annotációnak nincs explicit `ErrorMessage`-e. Élőben MÉRVE (`POST /api/auth/register`,
      // 300 karakteres név) a felhasználó EZT látta a REGISZTRÁCIÓS űrlapon — vagyis a
      // legnyilvánosabb felületünkön, angolul:
      //     „The field Name must be a string or array type with a maximum length of '100'..
      //      A jelszónak tartalmaznia kell…"
      // Kiváltható volt bármely >100 karakteres névvel, >255 e-maillel, >20 karakteres
      // telefonszámmal vagy >200 karakteres számlázási címmel.
      //
      // MIÉRT ELÉG MOST EGYETLEN SOR: az `apiErrorMessage` → `serverErrorMessage`
      // (`utils/apiErrors.ts`) 2026-09-14 óta MAGA olvassa a modell-validációs
      // `{ errors: { mező: [...] } }` alakot is — a külön ág tehát nem csak hibás volt, hanem
      // FELESLEGES is. Ráadásul a `message`-ág is helyreáll: a backend 400-as válasza ma már
      // magyar `message`-t is ad (`Program.cs` `InvalidModelStateResponseFactory`).
      setError(apiErrorMessage(err, 'Regisztrációs hiba történt. Kérjük, próbáld újra később.'));
    }
  };

  const missingRequirements = getMissingRequirements();
  const isSubmitDisabled = loading || missingRequirements.length > 0;
  const missingRequirementsText = missingRequirements.length > 0
    ? `Hiányzik: ${missingRequirements.join(', ')}`
    : '';

  /**
   * HOZZÁÉRT-E MÁR A FELHASZNÁLÓ AZ ŰRLAPHOZ? [B-W1, 2026-09-16]
   *
   * MI VOLT A BAJ: a „Hiányzik: …" jegyzék `error` (PIROS) színnel jelent meg abban a
   * pillanatban, ahogy a regisztráció-képernyő megnyílt — vagyis egyetlen leütés ELŐTT a
   * felhasználó egy vörös hibalistát látott kilenc hiányzó mezőről. Ez nem hiba volt, hanem
   * a kitöltés MÉG EL SEM KEZDŐDÖTT állapota: a vörös szín itt a felhasználót vádolta olyan
   * mulasztással, amit nem követhetett el. (A mezőnkénti hibák eddig is helyesen vártak a
   * `touched`-re: `:397` `error={touched.password && …}` — csak ez az ÖSSZESÍTŐ lista nem.)
   *
   * A JAVÍTÁS: a szín a hozzáéréstől függ, a SZÖVEG NEM tűnik el. Hozzáérés előtt semleges
   * (tájékoztató) színnel áll ott, hogy a felhasználó mégis lássa, mi kell a regisztrációhoz.
   *
   * ⚠️ MIÉRT NEM CSAK `touched`: a `touched` kizárólag BLUR-re állítódik (`handleBlur`, :185),
   * tehát aki gépel, de még nem lépett ki a mezőből, „hozzá nem értnek" számítana. Ezért az
   * első LEÜTÉS is számít: bármelyik szöveges mezőben lévő tartalom, illetve a szabályzat
   * pipája is hozzáérésnek minősül.
   */
  const hasInteracted =
    Object.values(touched).some(Boolean)
    || Boolean(
      formData.name || formData.email || formData.phone || formData.dateOfBirth || formData.password
      || formData.confirmPassword || formData.billingName || formData.billingZipCode
      || formData.billingCity || formData.billingStreetAddress || formData.taxNumber
      || formData.acceptTerms
    );

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%', position: 'relative' }}>
          {onBack && (
            <IconButton
              onClick={onBack}
              sx={{ position: 'absolute', top: 8, left: 8 }}
              aria-label="Vissza"
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            Regisztráció
          </Typography>

          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Csak az alapvető információkat kérjük. További adatokat később az Adatlap menüpontban adhatsz meg.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {isRegistered ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Sikeres regisztráció!
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Elküldtünk egy megerősítő e-mailt a megadott címre. Kérjük, kattints a levélben található linkre az e-mail cím megerősítéséhez.
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Az e-mail megerősítése után az adminisztrátor jóváhagyja a fiókodat, amiről e-mailben kapsz értesítést.
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  Addig kérjük, légy türelemmel!
                </Typography>
              </Alert>
              <Button
                variant="contained"
                onClick={onSwitchToLogin}
                sx={{ mt: 1 }}
              >
                Vissza a bejelentkezéshez
              </Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <TextField
                margin="normal"
                required
                fullWidth
                id="name"
                label="Játékos neve"
                name="name"
                autoComplete="name"
                autoFocus
                value={formData.name}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={loading}
                data-testid="register-name-input"
                error={touched.name && !!fieldErrors.name}
                helperText={touched.name && fieldErrors.name ? fieldErrors.name : 'Add meg a teljes nevedet'}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="E-mail cím"
                name="email"
                type="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={loading}
                data-testid="register-email-input"
                error={touched.email && !!fieldErrors.email}
                helperText={touched.email && fieldErrors.email ? fieldErrors.email : undefined}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="phone"
                label="Telefonszám"
                name="phone"
                autoComplete="tel"
                value={formData.phone}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={loading}
                data-testid="register-phone-input"
                error={touched.phone && !!fieldErrors.phone}
                helperText={touched.phone && fieldErrors.phone ? fieldErrors.phone : 'Például: +36301234567'}
              />
              {/* Születési dátum [Web regisztráció születési dátum, 2026-10-04]: natív `type="date"`, ugyanaz a
                  minta, mint az Adatlap szerkesztőjében (`UserProfile.tsx` „Születési dátum") — a payload
                  `yyyy-MM-dd` szöveg, amit a böngésző maga ad (nincs Date-konverzió). A `max` = a MAI helyi nap. */}
              <TextField
                margin="normal"
                required
                fullWidth
                id="dateOfBirth"
                label="Születési dátum"
                name="dateOfBirth"
                type="date"
                value={formData.dateOfBirth}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={loading}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: DOB_MIN, max: dobMax() }}
                data-testid="register-dateofbirth-input"
                error={touched.dateOfBirth && !!fieldErrors.dateOfBirth}
                helperText={touched.dateOfBirth && fieldErrors.dateOfBirth ? fieldErrors.dateOfBirth : 'Kiskorú tagoknál a szülői adatokat az Adatlapon kérjük'}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Jelszó"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="new-password"
                value={formData.password}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={loading}
                data-testid="register-password-input"
                error={touched.password && !!fieldErrors.password}
                /* A súgó a MAI szabályt mondja [K-3, 2026-09-16]. ITT KORÁBBAN „speciális
                   karakter (@$!%*?&)" állt: ez a hét jelre szűkítés FÉLREVEZETŐ volt (a `-`,
                   `_`, `.` nem volt elfogadva, az ékezetes betű pedig a teljes jelszót
                   elutasította) — mostantól bármilyen nem betű/szám karakter megteszi. */
                helperText={touched.password && fieldErrors.password ? fieldErrors.password : 'Legalább 6 karakter, kisbetű, nagybetű, szám és legalább egy nem betű/szám karakter (pl. ! ? - _ .)'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label="Jelszó megerősítése"
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                autoComplete="new-password"
                value={formData.confirmPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={loading}
                data-testid="register-confirm-password-input"
                error={touched.confirmPassword && !!fieldErrors.confirmPassword}
                helperText={touched.confirmPassword && fieldErrors.confirmPassword ? fieldErrors.confirmPassword : undefined}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                Számlázási adatok
              </Typography>

              <TextField
                margin="normal"
                required
                fullWidth
                id="billingName"
                label="Számlázási név"
                name="billingName"
                value={formData.billingName}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={loading}
                error={touched.billingName && !!fieldErrors.billingName}
                helperText={touched.billingName && fieldErrors.billingName ? fieldErrors.billingName : undefined}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  margin="normal"
                  required
                  id="billingZipCode"
                  label="Irányítószám"
                  name="billingZipCode"
                  value={formData.billingZipCode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={loading}
                  error={touched.billingZipCode && !!fieldErrors.billingZipCode}
                  helperText={touched.billingZipCode && fieldErrors.billingZipCode ? fieldErrors.billingZipCode : undefined}
                  sx={{ width: '30%' }}
                />
                <TextField
                  margin="normal"
                  required
                  id="billingCity"
                  label="Város"
                  name="billingCity"
                  value={formData.billingCity}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={loading}
                  error={touched.billingCity && !!fieldErrors.billingCity}
                  helperText={touched.billingCity && fieldErrors.billingCity ? fieldErrors.billingCity : undefined}
                  sx={{ width: '70%' }}
                />
              </Box>
              <TextField
                margin="normal"
                required
                fullWidth
                id="billingStreetAddress"
                label="Utca, házszám"
                name="billingStreetAddress"
                value={formData.billingStreetAddress}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={loading}
                error={touched.billingStreetAddress && !!fieldErrors.billingStreetAddress}
                helperText={touched.billingStreetAddress && fieldErrors.billingStreetAddress ? fieldErrors.billingStreetAddress : undefined}
              />
              <TextField
                margin="normal"
                fullWidth
                id="taxNumber"
                label="Adószám (opcionális)"
                name="taxNumber"
                value={formData.taxNumber || ''}
                onChange={handleChange}
                disabled={loading}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.acceptTerms}
                    onChange={(e) => setFormData(prev => ({ ...prev, acceptTerms: e.target.checked }))}
                    disabled={loading}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    Elolvastam és elfogadom a{' '}
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={(e) => {
                        e.preventDefault();
                        setTermsDialogOpen(true);
                      }}
                      sx={{ fontWeight: 'bold' }}
                    >
                      Részvételi Szabályzatot
                    </Link>
                  </Typography>
                }
                sx={{ mt: 2 }}
              />

              {/* describeChild: a Tooltip a címet aria-describedby-ként köti (nem aria-label-ként a span-ra —
                  az generikus elemen ARIA-szabálysértés, és a mezőnevek listája elnyelte volna a label-alapú
                  kereséseket). A letiltott gombon csak wrapper span-nal működik (nem kap egér-eseményt). */}
              <Tooltip title={missingRequirementsText} disableHoverListener={!missingRequirementsText} describeChild>
                <span style={{ display: 'block' }}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{ mt: 2, mb: missingRequirementsText ? 0.5 : 2 }}
                    disabled={isSubmitDisabled}
                    data-testid="register-submit-button"
                  >
                    {loading ? <CircularProgress size={24} /> : 'Regisztráció'}
                  </Button>
                </span>
              </Tooltip>
              {missingRequirementsText && (
                <FormHelperText
                  // A szín a HOZZÁÉRÉSTŐL függ, nem a hiánytól [B-W1, 2026-09-16] — lásd a
                  // `hasInteracted` melletti indoklást. Érintetlen űrlapon semleges
                  // (`text.secondary`), az első leütés/blur után hibaszín.
                  error={hasInteracted}
                  sx={{ textAlign: 'center', mb: 1.5, ...(hasInteracted ? {} : { color: 'text.secondary' }) }}
                  data-testid="register-missing-requirements"
                >
                  {missingRequirementsText}
                </FormHelperText>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button onClick={onSwitchToLogin} disabled={loading} data-testid="switch-to-login-button">
                  Van már fiókod? Jelentkezz be
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>

      <Dialog
        open={termsDialogOpen}
        onClose={() => setTermsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Részvételi Szabályzat</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {TERMS_TEXT}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTermsDialogOpen(false)}>
            Bezárás
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Register;
